import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, TrendingDown, Activity, ChevronDown, ChevronRight, Play, DollarSign, BarChart3, Target, Package } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { useApi } from '../hooks/useApi';

const V5_PROBABILISTIC_MODEL = 'v5.0.0';
const V5_DEFAULTS = {
  bucket1_volume: 20,
  bucket2_volume: 40,
  bucket3_volume: 60,
  preds_diff_cutoff: 5,
  prediction_filter_margin: 1000,
  apply_ridge_shift: false
};

const BisonModel = () => {
  const { fetchPost } = useApi();
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate dates: 2 weeks back for start_date, tomorrow for end_date
  const getDefaultDates = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    return {
      start: formatDate(twoWeeksAgo),
      end: formatDate(tomorrow)
    };
  };

  const defaultDates = getDefaultDates();

  const [formData, setFormData] = useState({
    start_date: defaultDates.start,
    end_date: defaultDates.end,
    fix_i_model_spread: 5,
    base_volume: 50,
    fix_i_model: 'v2.0.0',
    fix_ii_model: 'v1.0.0-fix2',
    probabilistic_model: 'v3.1.0',
    fix_ii_margin: 2500,
    preds_diff_cutoff: 10,
    bucket1_volume: 20,
    bucket2_volume: 40,
    bucket3_volume: 60,
    prediction_filter_margin: 10,
    np_constraint_bucket1: 0,
    np_constraint_bucket2: 0,
    np_constraint_bucket3: 0,
    apply_ridge_shift: false
  });


  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [displayHours1to24, setDisplayHours1to24] = useState(true);
  const [, setShowHourlyPerformance] = useState(false);
  const [hourlyPerformanceData, setHourlyPerformanceData] = useState([]);

  // Automatically calculate hourly performance when results are available
  useEffect(() => {
    if (!results || !results.results) {
      return;
    }

    const hourlyPnL = {};
    for (let i = 0; i < 24; i++) {
      hourlyPnL[i] = 0;
    }

    Object.values(results.results).forEach(dayData => {
      if (formData.apply_ridge_shift && dayData.shift_available === false) return;
      if (dayData.hourly_results && Array.isArray(dayData.hourly_results)) {
        dayData.hourly_results.forEach(hourData => {
          if (hourData.hourly_pnl !== null && hourData.hourly_pnl !== undefined) {
            hourlyPnL[hourData.hour] += hourData.hourly_pnl;
          }
        });
      }
    });

    const chartData = Object.entries(hourlyPnL).map(([hour, pnl]) => ({
      hour: displayHours1to24 ? parseInt(hour) + 1 : parseInt(hour),
      pnl: parseFloat(pnl.toFixed(2))
    }));

    setHourlyPerformanceData(chartData);
    setShowHourlyPerformance(true);
  }, [displayHours1to24, formData.apply_ridge_shift, results]);

  useEffect(() => {
    if (formData.probabilistic_model !== V5_PROBABILISTIC_MODEL) {
      return;
    }

    setFormData((prev) => {
      const next = {
        ...prev,
        ...V5_DEFAULTS
      };
      const changed = Object.keys(V5_DEFAULTS).some((key) => next[key] !== prev[key]);
      return changed ? next : prev;
    });
  }, [formData.probabilistic_model]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const runCombinedAnalysis = async (overrideFormData = null) => {
    setIsLoading(true);
    setError(null);
    setResults(null);
    setShowHourlyPerformance(false);
    setHourlyPerformanceData([]);
    
    try {
      const dataToUse = overrideFormData || formData;
      
      const formDataToSend = new FormData();
      Object.entries(dataToUse).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetchPost('/bison-results', formDataToSend);

      const data = await response.json();
      
      if (data.status === 'success') {
        setResults(data);
      } else if (data.detail) {
        // Handle validation errors
        setError('Validation error: ' + JSON.stringify(data.detail));
      } else {
        setError(data.message || 'Analysis failed');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Error running combined analysis:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRow = (date) => {
    setExpandedRows(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  const calculateDailyTradingSummary = (data) => {
    let summary = {
      base: {
        fix1: { buy: 0, sell: 0 },
        fix2: { buy: 0, sell: 0 }
      },
      hourly: {
        fix1: { buy: 0, sell: 0 },
        fix2: { buy: 0, sell: 0 }
      }
    };

    // Base trading calculations
    if (data.base_action_results && Array.isArray(data.base_action_results)) {
      data.base_action_results.forEach(hour => {
        // Fix I base actions
        if (hour.base_fix_i_action && hour.base_fix_i_volume > 0) {
          const value = hour.fix_i_price * hour.base_fix_i_volume;
          if (hour.base_fix_i_action === 'buy') {
            summary.base.fix1.buy += value;
          } else if (hour.base_fix_i_action === 'sell') {
            summary.base.fix1.sell += value;
          }
        }
        
        // Fix II base actions
        if (hour.base_fix_ii_action && hour.base_fix_ii_volume > 0) {
          const value = hour.fix_ii_price * hour.base_fix_ii_volume;
          if (hour.base_fix_ii_action === 'buy') {
            summary.base.fix2.buy += value;
          } else if (hour.base_fix_ii_action === 'sell') {
            summary.base.fix2.sell += value;
          }
        }
      });
    }

    // Hourly trading calculations
    if (data.hourly_results && Array.isArray(data.hourly_results)) {
      data.hourly_results.forEach(hour => {
        const fix_i_exec = hour.fix_i_execution || {};
        const fix_ii_exec = hour.fix_ii_execution || {};

        // Fix I hourly actions
        if (fix_i_exec.action && fix_i_exec.total_volume > 0) {
          const value = hour.fix_i_price * fix_i_exec.total_volume;
          if (fix_i_exec.action === 'buy') {
            summary.hourly.fix1.buy += value;
          } else if (fix_i_exec.action === 'sell') {
            summary.hourly.fix1.sell += value;
          }
        }

        // Fix II hourly actions
        if (fix_ii_exec.action && fix_ii_exec.volume > 0) {
          const value = hour.fix_ii_price * fix_ii_exec.volume;
          if (fix_ii_exec.action === 'buy') {
            summary.hourly.fix2.buy += value;
          } else if (fix_ii_exec.action === 'sell') {
            summary.hourly.fix2.sell += value;
          }
        }
      });
    }

    return summary;
  };

  const renderBaseActionsTable = (data, date) => {
    const baseActionData = data.base_action_results || [];

    if (!data.base_action || data.base_action === 'none' || baseActionData.length === 0) {
      return (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Base Actions</h3>
          <p className="text-gray-500 text-sm">No base actions taken</p>
        </div>
      );
    }

    const ordersData = baseActionData.filter(h => h.base_fix_i_action || h.base_fix_ii_action);
    if (ordersData.length === 0) return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Base Actions</h3>
        <p className="text-gray-500 text-sm">No base orders executed</p>
      </div>
    );

    // Always use hourly display for Base Actions (one row per hour)
    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Base Actions</h3>
        <div className="mb-2 p-2 bg-gray-100 rounded">
          <span className="text-sm text-gray-700">
            Initial {data.base_action} of {data.base_volume} units at base price {formatNumber(data.base_price)} for all hours
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hour</th>
                <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Model Pred</th>
                <th colSpan="5" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Fix I</th>
                <th colSpan="4" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Fix II</th>
                <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">PnL</th>
                <th rowSpan="2" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Residual</th>
              </tr>
              <tr>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Action</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order Vol</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Executed</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Action</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Volume</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ordersData.map((row, idx) => {
                const residual = row.base_residual || 0;
                const bgColor = row.base_pnl > 0 ? 'bg-green-50' : row.base_pnl < 0 ? 'bg-red-50' : '';

                return (
                  <tr key={idx} className={bgColor}>
                    <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {displayHours1to24 ? row.hour + 1 : row.hour}:00
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.fix_ii_prediction)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center border-l">
                      {row.base_fix_i_action && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          row.base_fix_i_action === 'buy' ? 'bg-green-200 text-green-800' :
                          row.base_fix_i_action === 'sell' ? 'bg-red-200 text-red-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {row.base_fix_i_action}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.base_fix_i_order_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.base_fix_i_volume || 0)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.fix_i_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {row.base_fix_i_volume > 0 ? (
                        <span className="text-green-600">✓</span>
                      ) : (
                        <span className="text-red-600">✗</span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center border-l">
                      {row.base_fix_ii_action && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          row.base_fix_ii_action === 'buy' ? 'bg-green-200 text-green-800' :
                          row.base_fix_ii_action === 'sell' ? 'bg-red-200 text-red-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {row.base_fix_ii_action}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.base_fix_ii_order_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.fix_ii_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {formatNumber(row.base_fix_ii_volume || 0)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right font-semibold ${
                      row.base_pnl > 0 ? 'text-green-600' :
                      row.base_pnl < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {formatNumber(row.base_pnl)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {residual !== 0 && (
                        <span className="text-orange-600 font-medium">{residual}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderHourlyTradingTable = (hourlyData, date) => {
    if (!hourlyData || hourlyData.length === 0) return (
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Step Order Execution</h3>
        <p className="text-gray-500 text-sm">No hourly trading data</p>
      </div>
    );

    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Step Order Execution</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hour</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix I Price</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix II Price</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border-l">Q05</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q10</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q20</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q30</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q40</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q50</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q60</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q70</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q80</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q90</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Q95</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Fix I Action</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Trend</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Volume</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Fix II Exec</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">PnL</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {hourlyData.map((hour, idx) => {
                const fix_i_exec = hour.fix_i_execution || {};
                const fix_ii_exec = hour.fix_ii_execution || {};
                const quantiles = hour.quantiles || {};
                const pnl = hour.hourly_pnl || 0;

                // Determine which quantiles triggered
                const triggeredBuyQuantiles = [];
                const triggeredSellQuantiles = [];

                if (fix_i_exec.orders_executed) {
                  fix_i_exec.orders_executed.forEach((order) => {
                    // Use actual_quantile for display (the shifted value), fallback to quantile for backwards compatibility
                    const displayQuantile = order.actual_quantile || order.quantile;
                    if (fix_i_exec.action === 'buy') {
                      triggeredBuyQuantiles.push(displayQuantile);
                    } else if (fix_i_exec.action === 'sell') {
                      triggeredSellQuantiles.push(displayQuantile);
                    }
                  });
                }

                // Determine which quantiles are active for orders based on shift direction
                // shiftDirection: 1 = down (Fix I pred > Fix II pred), -1 = up (Fix II pred > Fix I pred), 0 = no shift
                const getActiveOrderQuantiles = (shift) => {
                  if (shift > 0) {
                    // Shift down: buy q05,q10,q20; sell q60,q70,q80
                    return ['q05', 'q10', 'q20', 'q60', 'q70', 'q80'];
                  } else if (shift < 0) {
                    // Shift up: buy q20,q30,q40; sell q80,q90,q95
                    return ['q20', 'q30', 'q40', 'q80', 'q90', 'q95'];
                  } else {
                    // No shift: buy q10,q20,q30; sell q70,q80,q90
                    return ['q10', 'q20', 'q30', 'q70', 'q80', 'q90'];
                  }
                };

                // Check if quantiles were shifted based on prediction comparison
                const shiftDirection = hour.shift_direction;
                const activeQuantiles = getActiveOrderQuantiles(shiftDirection);

                const getCellClass = (quantile, price, fixPrice) => {
                  let classes = [];

                  // Add diagonal stripes for active order quantiles
                  if (activeQuantiles.includes(quantile)) {
                    classes.push('bg-stripes');
                  }

                  if (triggeredBuyQuantiles.includes(quantile)) {
                    classes.push('bg-green-100 font-semibold');
                  } else if (triggeredSellQuantiles.includes(quantile)) {
                    classes.push('bg-red-100 font-semibold');
                  } else {
                    // Highlight potential triggers that didn't execute (buy quantiles)
                    if (['q05', 'q10', 'q20', 'q30', 'q40'].includes(quantile) && fixPrice < price) {
                      classes.push('bg-green-50');
                    }
                    // Highlight potential triggers that didn't execute (sell quantiles)
                    if (['q60', 'q70', 'q80', 'q90', 'q95'].includes(quantile) && fixPrice > price) {
                      classes.push('bg-red-50');
                    }
                  }

                  return classes.join(' ');
                };
                const noAction = !fix_i_exec.action || fix_i_exec.action === 'none';

                return (
                  <tr key={idx} className={
                    pnl > 0 ? 'bg-green-50' :
                    pnl < 0 ? 'bg-red-50' :
                    noAction ? 'bg-gray-50' : ''
                  }>
                    <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {displayHours1to24 ? hour.hour + 1 : hour.hour}:00
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(hour.fix_i_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(hour.fix_ii_price)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 border-l ${getCellClass('q05', quantiles.q05, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q05)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q10', quantiles.q10, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q10)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q20', quantiles.q20, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q20)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q30', quantiles.q30, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q30)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q40', quantiles.q40, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q40)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                      {formatNumber(quantiles.q50)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q60', quantiles.q60, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q60)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q70', quantiles.q70, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q70)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q80', quantiles.q80, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q80)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q90', quantiles.q90, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q90)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900 ${getCellClass('q95', quantiles.q95, hour.fix_i_price)}`}>
                      {formatNumber(quantiles.q95)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {fix_i_exec.action ? (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          fix_i_exec.action === 'buy' ? 'bg-green-200 text-green-800' :
                          fix_i_exec.action === 'sell' ? 'bg-red-200 text-red-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {fix_i_exec.action}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-center">
                      {shiftDirection < 0 ? (
                        <span className="text-green-600 font-medium">up</span>
                      ) : shiftDirection > 0 ? (
                        <span className="text-red-600 font-medium">down</span>
                      ) : (
                        <span className="text-gray-400"></span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {fix_i_exec.total_volume || '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {fix_ii_exec.executed ? (
                        <span className="text-green-600">✓</span>
                      ) : fix_i_exec.action ? (
                        <span className="text-red-600">✗</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right font-semibold ${
                      pnl > 0 ? 'text-green-600' : pnl < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {formatNumber(pnl)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDateRow = (date, data) => {
    const isExpanded = expandedRows[date];
    const totalPnl = data.daily_pnl || 0;
    const basePnl = data.base_pnl || 0;
    const fixingPnl = data.probabilistic_pnl || 0;
    const totalVolume = data.total_volume_traded || 0;
    const shiftMissing = formData.apply_ridge_shift && data.shift_available === false;
    const isPnLPositive = !shiftMissing && totalPnl > 0;
    const isPnLNegative = !shiftMissing && totalPnl < 0;

    // Count hours with Fix II volume = 0 (for hourly trading)
    let zeroVolumeHoursCount = 0;

    if (data.hourly_results) {
      data.hourly_results.forEach(hour => {
        // Check if there's Fix II action for this hour with zero volume
        if (hour.fix_ii_action && hour.fix_ii_volume === 0) {
          zeroVolumeHoursCount++;
        }
      });
    }

    return (
      <div key={date} className="border rounded-lg mb-4 overflow-hidden">
        <div
          className={`p-4 cursor-pointer transition-colors overflow-x-auto ${
            shiftMissing ? 'bg-gray-100 hover:bg-gray-200 opacity-60' :
            isPnLPositive ? 'bg-green-50 hover:bg-green-100' :
            isPnLNegative ? 'bg-red-50 hover:bg-red-100' :
            'bg-gray-50 hover:bg-gray-100'
          }`}
          onClick={() => toggleRow(date)}
        >
          <div className="flex items-center justify-between min-w-max">
            <div className="flex items-center space-x-4">
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className={`font-semibold ${shiftMissing ? 'text-gray-400' : ''}`}>{date}</span>
              {shiftMissing && (
                <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full text-xs font-semibold">
                  No Ridge Shift
                </span>
              )}
              {data.base_missing && (
                <span className="px-2 py-1 bg-orange-200 text-orange-800 rounded-full text-xs font-semibold">
                  Base Missing
                </span>
              )}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                data.base_action === 'buy' ? 'bg-green-200 text-green-800' :
                data.base_action === 'sell' ? 'bg-red-200 text-red-800' :
                'bg-gray-200 text-gray-800'
              }`}>
                {data.base_action === 'none' ? 'idle' : data.base_action}
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {Math.round(totalVolume)} MWh
              </span>
              {/* Display count of hours with Fix II volume = 0 */}
              {zeroVolumeHoursCount > 0 && (
                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                  {zeroVolumeHoursCount} H
                </span>
              )}
            </div>
            <div className="flex flex-col space-y-1 text-sm">
              {/* First row: Base, Fix I Model Pred, Q50 Pred, Fix I, Fix II Pred, Fix II */}
              <div className="flex items-center space-x-4">
                <div>
                  <span className="text-gray-500">Base:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.base_price)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Model Pred:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.avg_q50_prediction)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Shift:</span>
                  <span className={`ml-2 font-semibold ${data.ridge_shift > 0 ? 'text-green-600' : data.ridge_shift < 0 ? 'text-red-600' : ''}`}>
                    {data.ridge_shift !== null && data.ridge_shift !== undefined ? `${data.ridge_shift > 0 ? '+' : ''}${data.ridge_shift}` : '-'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Fix I:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.avg_fix_i_price)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Fix II Pred:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.avg_fix_ii_prediction)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Fix II:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.avg_fix_ii_price)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Preds Diff:</span>
                  <span className={`ml-2 font-semibold ${
                    data.base_action_skipped_by_preds_diff || data.fixing_trades_skipped_by_preds_diff
                      ? (data.preds_diff > 0 ? 'text-red-600' : 'text-green-600')
                      : 'text-gray-900'
                  }`}>
                    {data.preds_diff !== null && data.preds_diff !== undefined
                      ? `${data.preds_diff > 0 ? '+' : ''}${data.preds_diff.toFixed(2)}%`
                      : 'N/A'}
                  </span>
                </div>
              </div>
              {/* Second row: Base PnL, Fixing PnL, Daily PnL */}
              <div className="flex items-center space-x-4">
                <div>
                  <span className="text-gray-500">Base PnL:</span>
                  <span className={`ml-2 font-semibold ${
                    basePnl > 0 ? 'text-green-600' : 
                    basePnl < 0 ? 'text-red-600' : 
                    'text-gray-600'
                  }`}>
                    {formatNumber(basePnl)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Fixing PnL:</span>
                  <span className={`ml-2 font-semibold ${
                    fixingPnl > 0 ? 'text-green-600' : 
                    fixingPnl < 0 ? 'text-red-600' : 
                    'text-gray-600'
                  }`}>
                    {formatNumber(fixingPnl)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Daily PnL:</span>
                  <span className={`ml-2 font-semibold ${
                    totalPnl > 0 ? 'text-green-600' : 
                    totalPnl < 0 ? 'text-red-600' : 
                    'text-gray-600'
                  }`}>
                    {formatNumber(totalPnl)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="p-4 bg-white border-t">
            {renderBaseActionsTable(data, date)}
            {renderHourlyTradingTable(data.hourly_results, date)}
            
            {/* Trading Summary for this date */}
            {(() => {
              const tradingSummary = calculateDailyTradingSummary(data);
              const baseBuyTotal = tradingSummary.base.fix1.buy + tradingSummary.base.fix2.buy;
              const baseSellTotal = tradingSummary.base.fix1.sell + tradingSummary.base.fix2.sell;
              const hourlyBuyTotal = tradingSummary.hourly.fix1.buy + tradingSummary.hourly.fix2.buy;
              const hourlySellTotal = tradingSummary.hourly.fix1.sell + tradingSummary.hourly.fix2.sell;
              
              // Calculate base action value
              const baseActionValue = data.base_price * 24 * data.base_volume;
              let baseActionBuyValue = 0;
              let baseActionSellValue = 0;
              
              if (data.base_action === 'buy') {
                baseActionBuyValue = baseActionValue;
              } else if (data.base_action === 'sell') {
                baseActionSellValue = baseActionValue;
              }
              
              const totalBuyValue = baseBuyTotal + hourlyBuyTotal + baseActionBuyValue;
              const totalSellValue = baseSellTotal + hourlySellTotal + baseActionSellValue;
              const pnl = totalSellValue - totalBuyValue;

              return (
                <div className="mt-6 border rounded-lg p-4 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Summary</h3>
                  
                  {/* Base Action Summary */}
                  {data.base_action && data.base_action !== 'none' && (
                    <div className="mb-4">
                      <h4 className="text-md font-semibold text-gray-700 mb-2">Base Action</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="border rounded p-3 bg-white">
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                {data.base_action === 'buy' ? 'Buy Value:' : 'Sell Value:'}
                              </span>
                              <span className={`font-semibold ${data.base_action === 'buy' ? 'text-green-600' : 'text-red-600'}`}>
                                {formatNumber(baseActionValue)}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              ({formatNumber(data.base_price)} × 24h × {data.base_volume} MW)
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Base Trading Summary */}
                  <div className="mb-4">
                    <h4 className="text-md font-semibold text-gray-700 mb-2">Base Trading</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                      <div className="border rounded p-3 bg-white">
                        <h5 className="font-medium text-gray-600 mb-2">Fix I</h5>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Buy Value:</span>
                            <span className="font-semibold text-green-600">{formatNumber(tradingSummary.base.fix1.buy)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sell Value:</span>
                            <span className="font-semibold text-red-600">{formatNumber(tradingSummary.base.fix1.sell)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="border rounded p-3 bg-white">
                        <h5 className="font-medium text-gray-600 mb-2">Fix II</h5>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Buy Value:</span>
                            <span className="font-semibold text-green-600">{formatNumber(tradingSummary.base.fix2.buy)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sell Value:</span>
                            <span className="font-semibold text-red-600">{formatNumber(tradingSummary.base.fix2.sell)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between items-center bg-green-50 p-2 rounded">
                        <span className="text-sm font-medium text-gray-700">Base Buy Value:</span>
                        <span className="font-bold text-green-700">{formatNumber(baseBuyTotal)}</span>
                      </div>
                      <div className="flex justify-between items-center bg-red-50 p-2 rounded">
                        <span className="text-sm font-medium text-gray-700">Base Sell Value:</span>
                        <span className="font-bold text-red-700">{formatNumber(baseSellTotal)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Hourly Trading Summary */}
                  <div className="mb-4">
                    <h4 className="text-md font-semibold text-gray-700 mb-2">Hourly Trading</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                      <div className="border rounded p-3 bg-white">
                        <h5 className="font-medium text-gray-600 mb-2">Fix I</h5>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Buy Value:</span>
                            <span className="font-semibold text-green-600">{formatNumber(tradingSummary.hourly.fix1.buy)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sell Value:</span>
                            <span className="font-semibold text-red-600">{formatNumber(tradingSummary.hourly.fix1.sell)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="border rounded p-3 bg-white">
                        <h5 className="font-medium text-gray-600 mb-2">Fix II</h5>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Buy Value:</span>
                            <span className="font-semibold text-green-600">{formatNumber(tradingSummary.hourly.fix2.buy)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sell Value:</span>
                            <span className="font-semibold text-red-600">{formatNumber(tradingSummary.hourly.fix2.sell)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between items-center bg-green-50 p-2 rounded">
                        <span className="text-sm font-medium text-gray-700">Hourly Buy Value:</span>
                        <span className="font-bold text-green-700">{formatNumber(hourlyBuyTotal)}</span>
                      </div>
                      <div className="flex justify-between items-center bg-red-50 p-2 rounded">
                        <span className="text-sm font-medium text-gray-700">Hourly Sell Value:</span>
                        <span className="font-bold text-red-700">{formatNumber(hourlySellTotal)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Final Totals */}
                  <div className="border-t pt-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex justify-between items-center bg-green-100 p-3 rounded">
                        <span className="font-medium text-gray-700">Total Buy Value:</span>
                        <span className="text-lg font-bold text-green-800">{formatNumber(totalBuyValue)}</span>
                      </div>
                      <div className="flex justify-between items-center bg-red-100 p-3 rounded">
                        <span className="font-medium text-gray-700">Total Sell Value:</span>
                        <span className="text-lg font-bold text-red-800">{formatNumber(totalSellValue)}</span>
                      </div>
                      <div className={`flex justify-between items-center p-3 rounded ${
                        pnl > 0 ? 'bg-green-100' : pnl < 0 ? 'bg-red-100' : 'bg-gray-100'
                      }`}>
                        <span className="font-medium text-gray-700">PnL:</span>
                        <span className={`text-lg font-bold ${
                          pnl > 0 ? 'text-green-800' : pnl < 0 ? 'text-red-800' : 'text-gray-800'
                        }`}>
                          {formatNumber(pnl)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Bison Model</h1>
          <p className="text-gray-600">Model with double shifting</p>
        </div>

        {/* Input Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>Start Date</span>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>End Date</span>
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Activity size={16} />
                <span>Fix I Model Spread</span>
              </label>
              <input
                type="number"
                value={formData.fix_i_model_spread}
                onChange={(e) => handleInputChange('fix_i_model_spread', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="0.1"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Package size={16} />
                <span>Base Volume</span>
              </label>
              <input
                type="number"
                value={formData.base_volume}
                onChange={(e) => handleInputChange('base_volume', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Fix I Model</span>
              </label>
              <select
                value={formData.fix_i_model}
                onChange={(e) => handleInputChange('fix_i_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v1.0.0">v1.0.0</option>
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                <option value="v1.0.1">v1.0.1</option>
                <option value="v1.0.2">v1.0.2</option>
                <option value="v1.0.3">v1.0.3</option>
                <option value="v1.0.3.1">v1.0.3.1</option>
                <option value="v2.0.0">v2.0.0</option>
                <option value="v2.0.0-cp14">v2.0.0-cp14</option>
                <option value="v2.0.0-exaa">v2.0.0-exaa</option>
                <option value="v2.0.0-hourly">v2.0.0-hourly</option>
                <option value="v2.0.0-l1">v2.0.0-l1</option>
                <option value="v2.0.0-narx">v2.0.0-narx</option>
                <option value="v2.0.0-narx-single">v2.0.0-narx-single</option>
                <option value="v4.0.0">v4.0.0</option>
                <option value="v4.1.0">v4.1.0</option>
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Fix II Model</span>
              </label>
              <select
                value={formData.fix_ii_model}
                onChange={(e) => handleInputChange('fix_ii_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                <option value="v1.0.0-fix2-cp14">v1.0.0-fix2-cp14</option>
                <option value="v1.0.0-fix2-exaa">v1.0.0-fix2-exaa</option>
                <option value="v2.0.0-fix2">v2.0.0-fix2</option>
                <option value="v2.0.0-l1-fix2">v2.0.0-l1-fix2</option>
                <option value="v2.0.0-narx-fix2">v2.0.0-narx-fix2</option>
                <option value="v4.0.0-fix2">v4.0.0-fix2</option>

              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Probabilistic Model</span>
              </label>
              <select
                value={formData.probabilistic_model}
                onChange={(e) => handleInputChange('probabilistic_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v3.0.0">v3.0.0</option>
                <option value="v3.1.0">v3.1.0</option>
                <option value="v3.1.1">v3.1.1</option>
                <option value="v3.2.0">v3.2.0</option>
                <option value="v3.3.0">v3.3.0</option>
                <option value="v4.0.0">v4.0.0</option>
                <option value="v4.1.0">v4.1.0</option>
                <option value="v5.0.0">v5.0.0</option>
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <DollarSign size={16} />
                <span>Fix II Margin</span>
              </label>
              <input
                type="number"
                value={formData.fix_ii_margin}
                onChange={(e) => handleInputChange('fix_ii_margin', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="1"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Preds Diff Cutoff (%)</span>
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.preds_diff_cutoff}
                onChange={(e) => handleInputChange('preds_diff_cutoff', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Target size={16} />
                <span>Trend Filter Margin (PLN)</span>
              </label>
              <input
                type="number"
                step="1"
                value={formData.prediction_filter_margin}
                onChange={(e) => handleInputChange('prediction_filter_margin', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Bucket Configuration */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Bucket Configuration (Arbitrage)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Bucket 1 Volume (MWh)</span>
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.bucket1_volume}
                  onChange={(e) => handleInputChange('bucket1_volume', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Bucket 2 Volume (MWh)</span>
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.bucket2_volume}
                  onChange={(e) => handleInputChange('bucket2_volume', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Bucket 3 Volume (MWh)</span>
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.bucket3_volume}
                  onChange={(e) => handleInputChange('bucket3_volume', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            {/* NP Constraint Thresholds */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Bucket 1 NP Constraint (MW)
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.np_constraint_bucket1}
                  onChange={(e) => handleInputChange('np_constraint_bucket1', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Bucket 2 NP Constraint (MW)
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.np_constraint_bucket2}
                  onChange={(e) => handleInputChange('np_constraint_bucket2', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Bucket 3 NP Constraint (MW)
                </label>
                <input
                  type="number"
                  step="1"
                  value={formData.np_constraint_bucket3}
                  onChange={(e) => handleInputChange('np_constraint_bucket3', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.apply_ridge_shift}
                onChange={(e) => handleInputChange('apply_ridge_shift', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Apply Ridge Shift</span>
            </label>
          </div>

          <div className="mt-4 flex gap-4">
            <button
              onClick={() => runCombinedAnalysis()}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-[1.02]'
              }`}
            >
              <Play size={20} />
              <span>{isLoading ? 'Running Analysis...' : 'Run Analysis'}</span>
            </button>
            
            <button
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const tomorrowStr = formatDate(tomorrow);
                const updatedFormData = {
                  ...formData,
                  start_date: tomorrowStr,
                  end_date: tomorrowStr
                };
                setFormData(updatedFormData);
                // Run analysis with the updated data
                runCombinedAnalysis(updatedFormData);
              }}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 transform hover:scale-[1.02]'
              }`}
            >
              <Calendar size={20} />
              <span>{isLoading ? 'Running Analysis...' : 'Run Analysis For Tomorrow'}</span>
            </button>
          </div>

          {/* Hour display format toggle */}
          <div className="mt-4 flex items-center space-x-3">
            <span className="text-sm text-gray-700">Display hours as 1-24</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={displayHours1to24}
                onChange={(e) => setDisplayHours1to24(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 border border-gray-400 shadow-sm peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:border-blue-500 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border after:border-gray-400 after:shadow-sm after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Results Summary */}
        {results && results.summary && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Summary</h2>
            
            {/* Key Metrics - First Row: Day Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Total Days</p>
                    <p className="text-2xl font-bold text-purple-800">
                      {results.summary.total_days || 0}
                    </p>
                  </div>
                  <Calendar className="text-purple-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Profitable Days</p>
                    <p className="text-2xl font-bold text-green-800">
                      {results.summary.profitable_days || 0}
                    </p>
                    <p className="text-xs text-green-600">
                      {formatPercentage(results.summary.profitable_days_percentage)} of total
                    </p>
                  </div>
                  <TrendingUp className="text-green-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 font-medium">Loss Days</p>
                    <p className="text-2xl font-bold text-red-800">
                      {results.summary.loss_days || 0}
                    </p>
                    <p className="text-xs text-red-600">
                      {formatPercentage(results.summary.loss_days_percentage)} of total
                    </p>
                  </div>
                  <TrendingDown className="text-red-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Second Row: Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600 font-medium">Buy Actions</p>
                    <p className="text-2xl font-bold text-emerald-800">
                      {results.summary.base_model_actions?.buy || 0}
                    </p>
                    <p className="text-xs text-emerald-600">
                      {formatPercentage(results.summary.base_model_actions_percentages?.buy)} of actions
                    </p>
                  </div>
                  <TrendingUp className="text-emerald-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-rose-600 font-medium">Sell Actions</p>
                    <p className="text-2xl font-bold text-rose-800">
                      {results.summary.base_model_actions?.sell || 0}
                    </p>
                    <p className="text-xs text-rose-600">
                      {formatPercentage(results.summary.base_model_actions_percentages?.sell)} of actions
                    </p>
                  </div>
                  <TrendingDown className="text-rose-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Idle Days</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {results.summary.base_model_actions?.none || 0}
                    </p>
                    <p className="text-xs text-gray-600">
                      {formatPercentage(results.summary.base_model_actions_percentages?.none)} of actions
                    </p>
                  </div>
                  <Activity className="text-gray-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Average Daily PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.avg_daily_combined_pnl >= 0 ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {formatNumber(results.summary.avg_daily_combined_pnl)}
                    </p>
                  </div>
                  <BarChart3 className="text-blue-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Third Row: PnL and Volume Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {/* 1. Total Base PnL */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total Base PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.total_base_pnl >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.total_base_pnl)}
                    </p>
                  </div>
                  <DollarSign className="text-blue-500" size={32} />
                </div>
              </div>

              {/* 2. Total Fixing PnL */}
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-600 font-medium">Total Fixing PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.total_fixing_pnl >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.total_fixing_pnl)}
                    </p>
                  </div>
                  <Activity className="text-indigo-500" size={32} />
                </div>
              </div>

              {/* 3. Total Combined PnL */}
              <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-cyan-600 font-medium">Total Combined PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.total_combined_pnl >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.total_combined_pnl)}
                    </p>
                  </div>
                  <DollarSign className="text-cyan-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Fourth Row: Volume and Efficiency Part 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {/* 4. Base Volume Traded */}
              <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-violet-600 font-medium">Base Volume Traded</p>
                    <p className="text-2xl font-bold text-violet-800">
                      {formatNumber(results.summary.total_base_volume_mwh || 0)} MWh
                    </p>
                    <p className="text-xs text-violet-600">
                      BASE contract volume executed
                    </p>
                  </div>
                  <Package className="text-violet-500" size={32} />
                </div>
              </div>

              {/* 5. Profit per MWh Traded in BASE */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Profit/MWh in BASE</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.profit_per_mwh_base >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.profit_per_mwh_base || 0)} PLN/MWh
                    </p>
                    <p className="text-xs text-purple-600">
                      BASE trading efficiency
                    </p>
                  </div>
                  <Target className="text-purple-500" size={32} />
                </div>
              </div>

              {/* 6. Arbitrage Volume Traded */}
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-teal-600 font-medium">Arbitrage Volume Traded</p>
                    <p className="text-2xl font-bold text-teal-800">
                      {formatNumber(results.summary.total_arbitrage_volume_mwh || 0)} MWh
                    </p>
                    <p className="text-xs text-teal-600">
                      Hourly trading volume executed
                    </p>
                  </div>
                  <Activity className="text-teal-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Fifth Row: Volume and Efficiency Part 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* 7. Profit per MWh Traded in Arbitrage */}
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600 font-medium">Profit/MWh in Arbitrage</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.profit_per_mwh_arbitrage >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.profit_per_mwh_arbitrage || 0)} PLN/MWh
                    </p>
                    <p className="text-xs text-emerald-600">
                      Arbitrage trading efficiency
                    </p>
                  </div>
                  <Target className="text-emerald-500" size={32} />
                </div>
              </div>

              {/* 8. Total Volume Traded */}
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600 font-medium">Total Volume Traded</p>
                    <p className="text-2xl font-bold text-amber-800">
                      {formatNumber(results.summary.total_volume_traded_mwh || 0)} MWh
                    </p>
                    <p className="text-xs text-amber-600">
                      Combined BASE + Arbitrage
                    </p>
                  </div>
                  <Package className="text-amber-500" size={32} />
                </div>
              </div>

              {/* 9. Profit per MWh Traded (Overall) */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-medium">Profit per MWh Traded</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.profit_per_mwh >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.profit_per_mwh || 0)} PLN/MWh
                    </p>
                    <p className="text-xs text-orange-600">
                      Overall trading efficiency
                    </p>
                  </div>
                  <Target className="text-orange-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Sixth Row: Success Rate by Bucket */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* Bucket 1 Success Rate */}
              <div className="bg-gradient-to-br from-lime-50 to-lime-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-lime-600 font-medium">Bucket 1 (q30/q70)</p>
                    <p className="text-2xl font-bold text-lime-800">
                      {results.summary.bucket_stats?.bucket1?.percentage || 0}%
                    </p>
                    <p className="text-xs text-lime-600">
                      {results.summary.bucket_stats?.bucket1?.profitable || 0} of {results.summary.bucket_stats?.bucket1?.total || 0} hours profitable
                    </p>
                    <div className="mt-2 pt-2 border-t border-lime-200">
                      <p className="text-xs text-lime-700 font-medium">Quantile Accuracy:</p>
                      <p className="text-xs text-lime-600">
                        q30 (buy): {results.summary.bucket_stats?.bucket1?.lower_accuracy || 0}% ({results.summary.bucket_stats?.bucket1?.price_above_lower || 0}/{results.summary.bucket_stats?.bucket1?.lower_total || 0})
                      </p>
                      <p className="text-xs text-lime-600">
                        q70 (sell): {results.summary.bucket_stats?.bucket1?.upper_accuracy || 0}% ({results.summary.bucket_stats?.bucket1?.price_below_upper || 0}/{results.summary.bucket_stats?.bucket1?.upper_total || 0})
                      </p>
                    </div>
                  </div>
                  <TrendingUp className="text-lime-500" size={32} />
                </div>
              </div>

              {/* Bucket 2 Success Rate */}
              <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-sky-600 font-medium">Bucket 2 (q20/q80)</p>
                    <p className="text-2xl font-bold text-sky-800">
                      {results.summary.bucket_stats?.bucket2?.percentage || 0}%
                    </p>
                    <p className="text-xs text-sky-600">
                      {results.summary.bucket_stats?.bucket2?.profitable || 0} of {results.summary.bucket_stats?.bucket2?.total || 0} hours profitable
                    </p>
                    <div className="mt-2 pt-2 border-t border-sky-200">
                      <p className="text-xs text-sky-700 font-medium">Quantile Accuracy:</p>
                      <p className="text-xs text-sky-600">
                        q20 (buy): {results.summary.bucket_stats?.bucket2?.lower_accuracy || 0}% ({results.summary.bucket_stats?.bucket2?.price_above_lower || 0}/{results.summary.bucket_stats?.bucket2?.lower_total || 0})
                      </p>
                      <p className="text-xs text-sky-600">
                        q80 (sell): {results.summary.bucket_stats?.bucket2?.upper_accuracy || 0}% ({results.summary.bucket_stats?.bucket2?.price_below_upper || 0}/{results.summary.bucket_stats?.bucket2?.upper_total || 0})
                      </p>
                    </div>
                  </div>
                  <TrendingUp className="text-sky-500" size={32} />
                </div>
              </div>

              {/* Bucket 3 Success Rate */}
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-pink-600 font-medium">Bucket 3 (q10/q90)</p>
                    <p className="text-2xl font-bold text-pink-800">
                      {results.summary.bucket_stats?.bucket3?.percentage || 0}%
                    </p>
                    <p className="text-xs text-pink-600">
                      {results.summary.bucket_stats?.bucket3?.profitable || 0} of {results.summary.bucket_stats?.bucket3?.total || 0} hours profitable
                    </p>
                    <div className="mt-2 pt-2 border-t border-pink-200">
                      <p className="text-xs text-pink-700 font-medium">Quantile Accuracy:</p>
                      <p className="text-xs text-pink-600">
                        q10 (buy): {results.summary.bucket_stats?.bucket3?.lower_accuracy || 0}% ({results.summary.bucket_stats?.bucket3?.price_above_lower || 0}/{results.summary.bucket_stats?.bucket3?.lower_total || 0})
                      </p>
                      <p className="text-xs text-pink-600">
                        q90 (sell): {results.summary.bucket_stats?.bucket3?.upper_accuracy || 0}% ({results.summary.bucket_stats?.bucket3?.price_below_upper || 0}/{results.summary.bucket_stats?.bucket3?.upper_total || 0})
                      </p>
                    </div>
                  </div>
                  <TrendingUp className="text-pink-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Seventh Row: Bucket Analysis */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* Bucket 1 Analysis */}
              <div className="bg-gradient-to-br from-lime-50 to-lime-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-lime-600 font-medium">Bucket 1 Analysis</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-lime-700">When Right:</span>
                        <span className="text-base font-bold text-lime-800">
                          {formatNumber(results.summary.bucket_stats?.bucket1?.avg_profitable_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-lime-700">When Wrong:</span>
                        <span className="text-base font-bold text-lime-800">
                          {formatNumber(results.summary.bucket_stats?.bucket1?.avg_loss_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 pt-1 border-t border-lime-200">
                        <span className="text-xs text-lime-700">Volume:</span>
                        <span className="text-base font-bold text-lime-800">
                          {Math.round(results.summary.bucket_stats?.bucket1?.total_volume_mwh || 0)} MWh
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-lime-700">Profit/MWh:</span>
                        <span className="text-lg font-bold text-lime-900">
                          {formatNumber(results.summary.bucket_stats?.bucket1?.profit_per_mwh || 0)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-lime-600 mt-1">
                      Spread, volume & efficiency metrics
                    </p>
                  </div>
                  <Activity className="text-lime-500" size={32} />
                </div>
              </div>

              {/* Bucket 2 Analysis */}
              <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-sky-600 font-medium">Bucket 2 Analysis</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-sky-700">When Right:</span>
                        <span className="text-base font-bold text-sky-800">
                          {formatNumber(results.summary.bucket_stats?.bucket2?.avg_profitable_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-sky-700">When Wrong:</span>
                        <span className="text-base font-bold text-sky-800">
                          {formatNumber(results.summary.bucket_stats?.bucket2?.avg_loss_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 pt-1 border-t border-sky-200">
                        <span className="text-xs text-sky-700">Volume:</span>
                        <span className="text-base font-bold text-sky-800">
                          {Math.round(results.summary.bucket_stats?.bucket2?.total_volume_mwh || 0)} MWh
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-sky-700">Profit/MWh:</span>
                        <span className="text-lg font-bold text-sky-900">
                          {formatNumber(results.summary.bucket_stats?.bucket2?.profit_per_mwh || 0)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-sky-600 mt-1">
                      Spread, volume & efficiency metrics
                    </p>
                  </div>
                  <Activity className="text-sky-500" size={32} />
                </div>
              </div>

              {/* Bucket 3 Analysis */}
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-pink-600 font-medium">Bucket 3 Analysis</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-pink-700">When Right:</span>
                        <span className="text-base font-bold text-pink-800">
                          {formatNumber(results.summary.bucket_stats?.bucket3?.avg_profitable_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-pink-700">When Wrong:</span>
                        <span className="text-base font-bold text-pink-800">
                          {formatNumber(results.summary.bucket_stats?.bucket3?.avg_loss_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 pt-1 border-t border-pink-200">
                        <span className="text-xs text-pink-700">Volume:</span>
                        <span className="text-base font-bold text-pink-800">
                          {Math.round(results.summary.bucket_stats?.bucket3?.total_volume_mwh || 0)} MWh
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-pink-700">Profit/MWh:</span>
                        <span className="text-lg font-bold text-pink-900">
                          {formatNumber(results.summary.bucket_stats?.bucket3?.profit_per_mwh || 0)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-pink-600 mt-1">
                      Spread, volume & efficiency metrics
                    </p>
                  </div>
                  <Activity className="text-pink-500" size={32} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Results */}
        {results && results.results && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Daily Results</h2>
            <div className="space-y-2 overflow-x-auto">
              {Object.entries(results.results)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([date, data]) => renderDateRow(date, data))}
            </div>
            
            {/* Hourly Performance Chart */}
            {hourlyPerformanceData.length > 0 && (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Hourly Performance Analysis</h3>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="hour" 
                        label={{ value: 'Hour', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis 
                        label={{ value: 'PnL', angle: -90, position: 'insideLeft' }}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip formatter={(value) => formatNumber(value)} />
                      <ReferenceLine y={0} stroke="#666" />
                      <Bar dataKey="pnl">
                        {hourlyPerformanceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.pnl >= 0 ? "#10b981" : "#ef4444"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Positive Hours</p>
                    <p className="text-xl font-bold text-green-700">
                      {hourlyPerformanceData.filter(d => d.pnl > 0).length}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Negative Hours</p>
                    <p className="text-xl font-bold text-red-700">
                      {hourlyPerformanceData.filter(d => d.pnl < 0).length}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BisonModel;
